import { EASE_OUT, EASE_IN_OUT } from './easing';
import { trackInteraction } from '../../analytics';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { watchCollection } from '../../firestoreLive';
import { PlatformItem } from '../../types';
import CaseStudyModal from '../CaseStudyModal';
import VelocitySkew from './VelocitySkew';
import VelocityMarquee from '../VelocityMarquee';
import MarqueeWord from './MarqueeWord';
import ArrowMark from './ArrowMark';
import { useTouchActive } from './useTouchActive';

// ~200kB Rive runtime: only fetched when a card that uses it is hovered
const RiveHover = React.lazy(() => import('./RiveHover'));

/**
 * Project grid in the spirit of lusion.co's work section: media reveals behind
 * a wipe, tilts and drifts against the cursor, plays a loop on hover, and the
 * title rolls letter by letter into the accent colour.
 */

interface Media {
  img: string;
  /** muted loop that fades in on hover */
  video?: string;
  /** live Rive animation, mounted only on hover */
  riv?: string;
  /** crossfading set of stills, cycling while hovered */
  photos?: string[];
}

/**
 * Bundled artwork used when a Firestore item has no `image` of its own.
 * Pulled from each platform's own media so the cards show real work rather
 * than flat colour. An image set in the admin panel always wins.
 */
const FALLBACK_MEDIA: Record<string, Media> = {
  // its own showreel project, so the reel clip is the honest preview
  behance: { img: '/thumbs/behance.jpg', video: '/thumbs/behance.mp4' },
  // From the clip rather than the old 256-colour GIF, whose palette banding
  // no amount of resolution would fix. Flat vector art, so it upscales clean.
  dribbble: { img: '/thumbs/dribbble.webp', video: '/thumbs/dribbble.mp4' },
  // Rendered from the .riv itself at 4:3 — the artboard is not square, and the
  // old 512x512 PNG was a square export that had already cropped the sides
  // before the card cropped it again. This is the exact frame the hover
  // plays, so the swap on mouseenter is seamless.
  rive: { img: '/thumbs/rive.webp', riv: '/thumbs/frog.riv' },
  // Captured from the live game — Snowy Peak, level 55, solved. The still is
  // the board the moment before the plan runs and the clip is that same run,
  // one recording at one device pixel ratio, so the hover swap does not jump.
  // Seeding hj_level in localStorage is what reaches a board with something on
  // it; the tutorial level the game opens on is three bare blocks. The world
  // is in the filename because these files are cached for a day: a reshoot has
  // to land on a new name or nobody sees it.
  henrysjourney: { img: '/thumbs/henrys-journey-snow-run.webp', video: '/thumbs/henrys-journey-snow-run.mp4' },
  // the channel's latest upload, not the showreel
  aiworks: { img: '/thumbs/ai-works.jpg', video: '/thumbs/ai-works.mp4' },
  // crossfade through shots from the hamlt.com gallery. The four skate shots
  // are all Venice; the statue leads so the card does not read as one subject.
  photography: {
    img: '/thumbs/photography.jpg',
    photos: [
      '/thumbs/photography.jpg',
      '/thumbs/photo-1.webp',
      '/thumbs/photo-2.webp',
      '/thumbs/photo-3.webp',
      '/thumbs/photo-4.webp',
    ],
  },
};

const slug = (name: string) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Each character sits in a 1.2em window above a duplicate of itself. On hover
 * the pair slides up with a per-letter stagger, so the word re-types itself in
 * the accent colour. This is the move Lusion uses on their project titles.
 */
export const LetterRoll: React.FC<{ text: string; active: boolean }> = ({ text, active }) => (
  <>
    {/* Every glyph below is its own inline-block rendered twice, so anything
        reading the text layer (crawlers, translation, reader mode) serialises
        the title as "B B e e h h a a n n c c e e". aria-label fixes screen
        readers but not extraction, so carry one clean copy of the word and
        hide the animated stack from assistive tech entirely. */}
    <span className="sr-only">{text}</span>
    <span className="inline-flex flex-wrap" aria-hidden="true">
    {text.split('').map((ch, i) => (
      <span
        key={i}
        className="inline-block overflow-hidden align-bottom"
        style={{ height: '1.2em' }}
      >
        <span
          className="block will-change-transform"
          style={{
            transform: active ? 'translateY(-50%)' : 'translateY(0)',
            transition: 'transform 620ms cubic-bezier(0.35,0,0,1)',
            transitionDelay: `${i * 22}ms`,
          }}
        >
          <span className="block" style={{ lineHeight: '1.2em' }}>
            {ch === ' ' ? ' ' : ch}
          </span>
          <span className="block text-electric" style={{ lineHeight: '1.2em' }}>
            {ch === ' ' ? ' ' : ch}
          </span>
        </span>
      </span>
    ))}
    </span>
  </>
);

const Card: React.FC<{ item: PlatformItem; index: number; onOpen: () => void }> = ({ item, index, onOpen }) => {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hovered, setHovered] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  // The clip is not fetched until someone hovers, so for the first second of
  // the first hover there is nothing to show. Fading the element in on hover
  // alone puts an empty box over the still and reads as the thumbnail
  // disappearing rather than swapping; wait until it has frames.
  const [clipReady, setClipReady] = useState(false);

  // tilt + counter-drift of the media against the cursor
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const spring = { stiffness: 170, damping: 20, mass: 0.4 };
  const srx = useSpring(rx, spring);
  const sry = useSpring(ry, spring);
  const spx = useSpring(px, spring);
  const spy = useSpring(py, spring);

  const media: Media | undefined = item.image ? { img: item.image } : FALLBACK_MEDIA[slug(item.name)];

  // photo carousel: advance while hovered, crossfading between frames
  useEffect(() => {
    if (!hovered || !media?.photos || media.photos.length < 2) return;
    const iv = setInterval(() => setPhotoIdx((i) => (i + 1) % media.photos!.length), 1100);
    return () => clearInterval(iv);
  }, [hovered, media?.photos]);

  const onMove = (e: React.MouseEvent) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    ry.set(nx * 7);
    rx.set(-ny * 6);
    px.set(-nx * 18);
    py.set(-ny * 18);
  };

  const enter = () => {
    setHovered(true);
    const v = videoRef.current;
    if (v) {
      if (v.preload !== 'auto') v.preload = 'auto';
      v.play().catch(() => {});
    }
  };

  const leave = () => {
    setHovered(false);
    rx.set(0);
    ry.set(0);
    px.set(0);
    py.set(0);
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
  };

  // A phone never fires any of the above, so the preview, the photo carousel,
  // the Rive scene and the title roll would all sit frozen. The card activates
  // when it reaches the middle band of the viewport instead.
  const touchActive = useTouchActive(ref);
  useEffect(() => {
    if (!window.matchMedia('(hover: none)').matches) return;
    if (touchActive) enter();
    else leave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchActive]);

  const hasDetails = !!item.fullVideoId || !!item.description;
  const tags = (item.role || 'Motion · Design')
    .split(/[/,·|]/)
    .map((t) => t.trim())
    .filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-12% 0px' }}
      transition={{ duration: 0.9, delay: (index % 2) * 0.08, ease: EASE_OUT }}
      className="group"
    >
      <a
        href={item.link}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => {
          // Recorded either way: opening the case-study panel and leaving for
          // the real thing are both "this is the piece they wanted".
          trackInteraction('project_click', item.name);
          if (hasDetails) {
            e.preventDefault();
            onOpen();
          }
        }}
        data-cursor="VIEW"
        className="block"
      >
        <div style={{ perspective: 1100 }}>
          {/* The tilt and the rounded clip have to live on different elements.
              WebKit refuses to clip children to a border-radius when the
              clipping box is itself 3D-transformed, which is why these cards
              rendered with square corners on iPhone while Chrome looked fine. */}
          <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseEnter={enter}
            onMouseLeave={leave}
            style={{ rotateX: srx, rotateY: sry }}
            className="will-change-transform"
          >
          <div
            // Splitting the tilt off should be enough on its own, but this mask
            // forces WebKit to honour the radius even if the element is promoted
            // to its own layer, which is the failure mode that produced square
            // corners on iPhone. It is inert everywhere else.
            style={{ WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
            className="relative w-full aspect-[4/3] rounded-[20px] md:rounded-[28px] overflow-hidden bg-card"
          >
            {/* wipe-in reveal */}
            <motion.div
              className="absolute inset-0"
              initial={{ clipPath: 'inset(100% 0 0 0)' }}
              whileInView={{ clipPath: 'inset(0% 0 0 0)' }}
              viewport={{ once: true, margin: '-12% 0px' }}
              transition={{ duration: 1.1, delay: 0.1, ease: EASE_IN_OUT }}
            >
              {media ? (
                <>
                  {/* media drifts opposite the cursor and scales on hover */}
                  <motion.div className="absolute -inset-[8%]" style={{ x: spx, y: spy }}>
                    <img
                      src={media.img}
                      alt={item.name}
                      loading="lazy"
                      draggable={false}
                      className="absolute inset-0 w-full h-full object-cover ease-[cubic-bezier(0.35,0,0,1)] transition-transform duration-[900ms] group-hover:scale-[1.07]"
                    />
                    {/* crossfading photo set (photography card) */}
                    {media.photos &&
                      media.photos.map((src, pi) => (
                        <img
                          key={src}
                          src={src}
                          alt=""
                          loading="lazy"
                          draggable={false}
                          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                          style={{ opacity: hovered ? (pi === photoIdx ? 1 : 0) : pi === 0 ? 1 : 0 }}
                        />
                      ))}
                    {/* the actual Rive file plays on hover; the still stays beneath as fallback */}
                    {media.riv && hovered && (
                      <React.Suspense fallback={null}>
                        <div className="absolute inset-0 animate-[fadeIn_400ms_ease-out_forwards]">
                          <RiveHover src={media.riv} />
                        </div>
                      </React.Suspense>
                    )}
                    {media.video && (
                      <video
                        ref={videoRef}
                        src={media.video}
                        aria-label={`${item.name} preview`}
                        muted
                        loop
                        playsInline
                        preload="none"
                        onCanPlay={() => setClipReady(true)}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                          hovered && clipReady ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    )}
                  </motion.div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                </>
              ) : (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  // var(--color-card) rather than a copy of it, so a palette change
                    // reaches this gradient too
                    style={{
                      background: `linear-gradient(140deg, ${item.color || 'var(--color-card-raised)'} 0%, var(--color-card) 90%)`,
                    }}
                >
                  <span className="font-grotesk font-bold text-3xl md:text-4xl tracking-tight text-white/85">
                    {item.name}
                  </span>
                </div>
              )}
            </motion.div>

            {/* index, and an arrow that slides in on hover */}
            <span className="absolute left-5 top-4 font-grotesk text-[10px] uppercase tracking-[0.25em] text-white/70 mix-blend-difference">
              {String(index + 1).padStart(2, '0')}
            </span>
            <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-white text-ink flex items-center justify-center text-lg opacity-0 -translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-[cubic-bezier(0.35,0,0,1)]">
              <ArrowMark />
            </div>
          </div>
          </motion.div>
        </div>

        <div className="mt-4 md:mt-5 px-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
            {tags.map((t, i) => (
              <span key={i} className="font-grotesk text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-ink/65">
                {t}
                {i < tags.length - 1 && <span className="ml-2 text-ink/25">•</span>}
              </span>
            ))}
          </div>
          <h3 className="font-grotesk font-semibold text-2xl md:text-[2rem] leading-none tracking-tight text-ink">
            <LetterRoll text={item.name} active={hovered} />
          </h3>
        </div>
      </a>
    </motion.div>
  );
};

const WorkGrid: React.FC = () => {
  const [items, setItems] = useState<PlatformItem[]>([]);
  const [selected, setSelected] = useState<PlatformItem | null>(null);

  useEffect(() => {
    const unsub = watchCollection<PlatformItem>('links', (data) => {
      const visible = data.filter((i) => i.isVisible !== false);
      const PRIORITY = ['behance', 'dribbble', 'rive', 'henry', 'ai', 'photo'];
      visible.sort((a, b) => {
        const ai = PRIORITY.findIndex((k) => (a.name || '').toLowerCase().includes(k));
        const bi = PRIORITY.findIndex((k) => (b.name || '').toLowerCase().includes(k));
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return 0;
      });
      setItems(visible);
    });
    return () => unsub();
  }, []);

  return (
    <>
      <section id="work" className="relative py-section">
        {/* running band entering the section — counter-direction to the
            toolkit line in About, and it eases down when you hover it */}
        <div className="border-y border-ink/10 py-4 md:py-6 mb-[9vh] md:mb-[12vh]">
          <VelocityMarquee speed={-72} className="select-none">
            {['Selected work', 'Motion', 'Film', '3D', 'XR'].map((w) => (
              <MarqueeWord key={w} outlined tone="ink">
                {w}
              </MarqueeWord>
            ))}
          </VelocityMarquee>
        </div>

        <div className="max-w-[92rem] mx-auto px-5 md:px-10">
          <div className="flex items-end justify-between mb-12 md:mb-20">
            <h2 className="font-grotesk font-semibold text-[11vw] md:text-[5.5vw] leading-none tracking-tight text-ink">
              Selected work
            </h2>
            <span className="hidden md:block font-grotesk text-xs uppercase tracking-[0.3em] text-ink/65 pb-3">
              001 · {items.length.toString().padStart(2, '0')} projects
            </span>
          </div>

          {/* Three columns above 1280px, and the cards get smaller with it.
              That is the only real lever on thumbnail sharpness here: the
              bundled art tops out around 1250px wide, and a two-column card on
              a retina 1440 asks for 1578 device pixels across, so every
              thumbnail was being upscaled between 1.3x and 3x. At three
              columns the same art covers the card almost exactly. */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14 md:gap-y-20 xl:gap-y-16">
            {items.map((item, i) => (
              <div
                key={item.id}
                // The dropped column has to follow the column count. At two it
                // is every other card; at three that same rule would offset
                // the middle of the first row and the left of the second, and
                // the rhythm falls apart — so the middle column drops instead.
                className={`${i % 2 === 1 ? 'md:mt-24' : ''} ${i % 3 === 1 ? 'xl:mt-24' : 'xl:mt-0'}`}
              >
                {/* left column shears one way, right column the other */}
                <VelocitySkew>
                  <Card item={item} index={i} onOpen={() => setSelected(item)} />
                </VelocitySkew>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* portalled to <body>: the scroll-skew transform on <main> would
          otherwise become this fixed modal's containing block */}
      {typeof document !== 'undefined' &&
        createPortal(
          <CaseStudyModal isOpen={!!selected} onClose={() => setSelected(null)} project={selected} />,
          document.body
        )}
    </>
  );
};

export default WorkGrid;
